// Lightweight env-var-gated analytics. No-ops if IDs are missing.
// Loaders are idempotent — safe to call on every mount.

const GA4_ID = process.env.REACT_APP_GA4_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;
// Google Ads global site tag. Account-level ID (public, not a secret).
// Hardcoded on purpose — DO NOT read it from process.env: reading a REACT_APP_*
// key that is UNSET at build time makes webpack's DefinePlugin inline the
// ENTIRE process.env object into the bundle (on Vercel that leaked
// REACT_APP_VERCEL_GIT_COMMIT_MESSAGE, deployment IDs, etc.).
// scripts/check-env-leak.mjs fails the build if this ever regresses.
const GOOGLE_ADS_ID = 'AW-860000073';

let ga4Loaded = false;
let pixelLoaded = false;
let clarityLoaded = false;
let adsLoaded = false;

function injectScript(src, attrs = {}) {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  return s;
}

export function loadGA4() {
  if (ga4Loaded || !GA4_ID || typeof window === 'undefined') return;
  ga4Loaded = true;
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  // AnalyticsGate owns the initial + SPA route pageviews. Auto-send here would
  // duplicate the first route because the gate runs immediately after init.
  window.gtag('config', GA4_ID, { send_page_view: false });
}

export function loadGoogleAds() {
  if (adsLoaded || !GOOGLE_ADS_ID || typeof window === 'undefined') return;
  adsLoaded = true;
  // Reuse the gtag.js loader if GA4 already pulled it in; otherwise load it.
  if (!window.gtag) {
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
  }
  window.gtag('config', GOOGLE_ADS_ID);
}

export function loadMetaPixel() {
  if (pixelLoaded || !META_PIXEL_ID || typeof window === 'undefined') return;
  pixelLoaded = true;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
  n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
  document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', META_PIXEL_ID);
}

export function loadClarity() {
  if (clarityLoaded || !CLARITY_ID || typeof window === 'undefined') return;
  clarityLoaded = true;
  /* eslint-disable */
  (function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
  })(window,document,"clarity","script",CLARITY_ID);
  /* eslint-enable */
}

export function initAnalytics() {
  loadGA4();
  loadGoogleAds();
  loadMetaPixel();
  loadClarity();
}

// Route tracking has one owner so the hard load and SPA transitions stay in
// lockstep across GA4 and Meta without duplicating the initial pageview.
export function trackPageView(params = {}) {
  if (typeof window === 'undefined') return;
  try {
    if (window.gtag) window.gtag('event', 'page_view', params);
  } catch (_) {}
  try {
    if (window.fbq) window.fbq('track', 'PageView');
  } catch (_) {}
}

// Unified event tracker. Only completed student leads and direct contact
// actions map to Meta standard events. Trial/lead CTA clicks remain intent
// events in GA4; Opus1 owns the authoritative submitted-lead and paid-Purchase
// conversions. Careers applications fire job_application_submitted, which is
// deliberately absent from pixelMap so it stays GA4-only and never pollutes
// Meta Lead optimization.
export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined') return;
  try {
    if (window.gtag) window.gtag('event', name, params);
  } catch (_) {}
  try {
    if (window.fbq) {
      const pixelMap = {
        lead_submitted: 'Lead',
        phone_click: 'Contact',
        sms_click: 'Contact',
      };
      const pixelEvent = pixelMap[name];
      if (pixelEvent) window.fbq('track', pixelEvent, params);
    }
  } catch (_) {}
}

const CTA_EVENTS = new Set([
  'trial_click',
  'lead_click',
  'phone_click',
  'sms_click',
  'email_click',
  'social_click',
  'room_reserve_click',
  'career_role_select',
]);

// CTA callers provide an explicit event name. Unknown names fail safely to a
// generic click instead of silently becoming a trial click / Meta Lead.
export function trackCta(eventName, location) {
  const name = CTA_EVENTS.has(eventName) ? eventName : 'cta_click';
  trackEvent(name, { cta_label: eventName, cta_location: location });
}
